// Delay between flushing buffered lines to create a smooth reading experience
const LINE_FLUSH_DELAY_MS = 100;

const delay = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

type BufferState = {
  readonly text: string;
  readonly id: string;
  readonly type: string | undefined;
};

const INITIAL_BUFFER: BufferState = { text: '', id: '', type: undefined };

type StreamChunk = Record<string, unknown> & {
  type: string;
  id: string;
  delta?: string;
};

const isTextDelta = (type: string) =>
  type === 'text-delta' || type === 'reasoning-delta';

const splitBufferedLines = (text: string) => {
  const parts = text.split('\n');

  return {
    lines: parts.slice(0, -1).map(part => `${part}\n`),
    remainder: parts.at(-1) ?? '',
  };
};

export const createSmoothingTransform = () => {
  let buffer: BufferState = INITIAL_BUFFER;

  return new TransformStream({
    async transform(rawChunk, controller) {
      const chunk: StreamChunk = rawChunk;
      if (!isTextDelta(chunk.type)) {
        if (buffer.text) {
          controller.enqueue({
            ...chunk,
            type: buffer.type,
            id: buffer.id,
            delta: buffer.text,
          });
        }
        buffer = INITIAL_BUFFER;
        controller.enqueue(chunk);
        return;
      }

      const { lines, remainder } = splitBufferedLines(
        buffer.text + (chunk.delta ?? '')
      );

      buffer = { text: remainder, id: chunk.id, type: chunk.type };

      lines
        .map(line => ({ ...chunk, delta: line }))
        .forEach(c => controller.enqueue(c));

      if (lines.length > 0) {
        await delay(LINE_FLUSH_DELAY_MS);
      }
    },
    flush(controller) {
      if (buffer.text && buffer.type) {
        controller.enqueue({
          type: buffer.type,
          id: buffer.id,
          delta: buffer.text,
        });
      }
    },
  });
};
